import { useState, useEffect } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlayCircle, Calendar, Users, Building, Bot, MessageCircle } from "lucide-react";
import { AuthService } from "@/lib/auth";
import type { User } from "@/lib/auth";
import { formatDateStandard } from "@servify/shared-utils";

// Flip Card CSS
const flipCardStyles = `
  .flip-card {
    perspective: 1000px;
    width: 100%;
    height: 192px;
  }
  
  .flip-card-inner {
    position: relative;
    width: 100%;
    height: 100%;
    text-align: center;
    transition: transform 0.8s;
    transform-style: preserve-3d;
  }
  
  .flip-card:hover .flip-card-inner {
    transform: rotateY(180deg);
  }
  
  .flip-card-front, .flip-card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    border-radius: 0.5rem;
  }
  
  .flip-card-back {
    transform: rotateY(180deg);
  }
`;

export default function CandidateDashboard() {



  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await AuthService.getCurrentUser();
        if (userData?.user) {
          setUser(userData.user);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Exit any existing picture-in-picture mode on component mount
  useEffect(() => {
    const exitPictureInPicture = async () => {
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        }
      } catch (error) {
        console.log('No picture-in-picture to exit');
      }
    };

    exitPictureInPicture();

    // Prevent any video from entering picture-in-picture
    const handleEnterpictureInPicture = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('enterpictureinpicture', handleEnterpictureInPicture);

    return () => {
      document.removeEventListener('enterpictureinpicture', handleEnterpictureInPicture);
    };
  }, []);

  // Q&A Knowledge Base
  const qaDatabase = {
    "company benefits": "Servify offers comprehensive benefits including health insurance, dental coverage, vision care, 401(k) with company match, unlimited PTO, flexible work arrangements, professional development budget, and wellness programs. We believe in taking care of our team! 🏥💼",
    "office locations": "Servify has offices in San Francisco (HQ), New York, London, and Bangalore. We also support remote work and have a hybrid work model. Each office is designed to foster collaboration and creativity! 🏢🌍",
    "it support": "Our IT team is available 24/7 to help with any technical issues. You'll receive a company laptop, necessary software, and access to our internal support portal. Contact us at it-support@servify.com or through the help desk! 💻🛠️",
    "core values": "Servify's core values are: 1) Customer First - We put our customers at the heart of everything, 2) Innovation - We embrace new ideas and technologies, 3) Integrity - We act with honesty and transparency, 4) Collaboration - We work together to achieve greatness, 5) Growth - We continuously learn and improve! 🎯✨",
    "onboarding": "Your onboarding journey includes: Week 1 - Company orientation and team introductions, Week 2-3 - Role-specific training and shadowing, Week 4 - First project assignment. You'll have a dedicated mentor throughout the process! 📚👥",
    "work culture": "Servify fosters a collaborative, inclusive culture where innovation thrives. We celebrate diversity, encourage work-life balance, and believe in having fun while doing great work. Regular team events, hackathons, and learning sessions keep our culture vibrant! 🎉🤝"
  };

  // Function to get answer for a question
  const getAnswer = (question: string): string => {
    const lowerQuestion = question.toLowerCase();
    for (const [key, answer] of Object.entries(qaDatabase)) {
      if (lowerQuestion.includes(key)) return answer;
    }
    return "I'm not sure about that specific question, but I'd be happy to help you find the right person to ask! You can also check our employee handbook or reach out to HR. 🤔📖";
  };

  // Function to format joining date
  const formatJoiningDate = (dateString?: string): string => {
    if (!dateString) return 'TBD';
    
    try {
      return formatDateStandard(dateString);
    } catch (error) {
      return 'TBD';
    }
  };




  
  // Show loading state while fetching user data
  if (loading) {
    return (
      <div className="space-y-8" data-testid="candidate-dashboard">
        <div className="bg-servify-purple rounded-2xl p-8 text-white" data-testid="candidate-welcome-section">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">Loading...</h1>
            <p className="text-servify-white-grey text-lg mb-6">Please wait while we load your information.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="w-12 h-12 bg-servify-light-grey rounded-lg mb-4"></div>
                  <div className="h-8 bg-servify-light-grey rounded mb-2"></div>
                  <div className="h-4 bg-servify-light-grey rounded"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="candidate-dashboard">
      <style dangerouslySetInnerHTML={{ __html: flipCardStyles }} />
      {/* Welcome Section */}
      <div className="bg-servify-purple rounded-2xl p-8 text-white" data-testid="candidate-welcome-section">
        <div className="flex-1">
          <h1 className="text-3xl font-bold mb-2" data-testid="text-candidate-welcome-title">
            Welcome to Servify, {user?.firstName || 'there'}!
          </h1>
          <p className="text-servify-white-grey text-lg mb-6" data-testid="text-candidate-welcome-description">
            Congratulations on your upcoming journey with us! Get ready to explore our company culture and values before your first day.
          </p>
          <div className="flex space-x-4">
            <Button 
              className="bg-white text-servify-purple hover:bg-servify-white-grey"
              onClick={() => window.open('https://servify.com/in', '_blank')}
              data-testid="button-explore-company"
            >
              Explore Company
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Layout - All Three in One Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Joining Date Card */}
        <Card className="border-servify-light-grey">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <Calendar className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple" data-testid="text-joining-date">
                {loading ? '...' : formatJoiningDate(user?.dateOfJoining)}
              </span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Joining Date</h3>
            <p className="text-servify-medium-grey text-sm">
              {loading ? '...' : (() => {
                if (!user?.dateOfJoining) return 'Your first day at Servify';
                const joiningDate = new Date(user.dateOfJoining);
                const today = new Date();
                const diffTime = joiningDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                if (diffDays < 0) {
                  return 'Welcome aboard!';
                } else if (diffDays === 0) {
                  return 'Today is your first day!';
                } else if (diffDays === 1) {
                  return '1 day to go!';
                } else {
                  return `${diffDays} days to go!`;
                }
              })()}
            </p>
          </CardContent>
        </Card>

        {/* Office Location Card */}
        <Card className="border-servify-light-grey">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                <Building className="text-servify-purple h-6 w-6" />
              </div>
              <span className="text-2xl font-bold text-servify-purple" data-testid="text-office-location">
                {loading ? '...' : (user?.workLocation && typeof user.workLocation === 'string' ? user.workLocation.charAt(0).toUpperCase() + user.workLocation.slice(1) : 'Mumbai')}
              </span>
            </div>
            <h3 className="font-semibold text-servify-dark-grey mb-1">Office Location</h3>
            <p className="text-servify-medium-grey text-sm">Your primary workplace</p>
          </CardContent>
        </Card>

        {/* Video Preview Card */}
        <Card className="border-servify-light-grey">
          <CardContent className="p-6">
            
            <div className="bg-servify-light-grey rounded-lg overflow-hidden">
              <video 
                controls
                className="w-full h-40 object-cover rounded-lg"
                preload="metadata"
                onLoadedMetadata={(e) => {
                  // Disable picture-in-picture
                  const video = e.currentTarget;
                  video.disablePictureInPicture = true;
                  if (video.requestPictureInPicture) {
                    video.removeAttribute('controls');
                    setTimeout(() => video.setAttribute('controls', 'true'), 100);
                  }
                }}
                onContextMenu={(e) => {
                  // Prevent right-click context menu that can trigger PiP
                  e.preventDefault();
                }}
                disablePictureInPicture={true}
              >
                <source src="https://img-nonprod.servify.tech/Testing/Transforming+Device+Ownership+Experience-VEED+%282%29.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Overview */}
      <Card className="border-servify-light-grey" data-testid="company-overview">
        <CardHeader className="border-b border-servify-light-grey">
          <CardTitle className="text-servify-purple">About Servify</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-servify-purple/5 rounded-lg">
              <h3 className="font-semibold text-servify-purple mb-2" data-testid="text-company-mission">Vision</h3>
              <p className="text-servify-dark-grey text-sm">
                To be the platform that brings together all eco-system partners to deliver consumer happiness through great after-sales service.
              </p>
            </div>
            
            <div className="p-4 bg-servify-purple/5 rounded-lg">
                              <h3 className="font-semibold text-servify-purple mb-2" data-testid="text-company-values">Core Values</h3>
                <ul className="text-servify-dark-grey text-sm space-y-1">
                  <li>• <strong>Conviction</strong>: Belief in our mission and purpose</li>
                  <li>• <strong>Courage</strong>: Facing challenges with bravery and determination</li>
                  <li>• <strong>Care</strong>: Genuine concern for customers and team</li>
                  <li>• <strong>Curiosity</strong>: Continuous learning and exploration</li>
                  <li>• <strong>Clarity</strong>: Transparent and precise communication</li>
                  <li>• <strong>Commitment</strong>: Dedication to delivering excellence</li>
                  <li>• <strong>Conquer</strong>: Overcoming obstacles to achieve goals</li>
                </ul>
            </div>

            <div className="p-4 bg-servify-purple/5 rounded-lg">
              <h3 className="font-semibold text-servify-purple mb-2" data-testid="text-company-ethos">Ethos</h3>
              <ul className="text-servify-dark-grey text-sm space-y-2">
                <li>
                  <div>• <strong>Data Driven Decisions</strong>:</div>
                  <div className="ml-4 mt-1">At Servify, every choice we make is powered by insights. We combine facts, customer feedback, and real data with our experience to solve problems smarter. This helps us stay agile, objective, and always ready to create the best outcomes together.</div>
                </li>
                <li>
                  <div>• <strong>Customer First</strong>:</div>
                  <div className="ml-4 mt-1">Our guiding principle is simple — the customer comes first. Whether it's a brand, partner, or end consumer, we design every journey around their needs. At Servify, delighting customers isn't just a goal, it's how we work every day.</div>
                </li>
                <li>
                  <div>• <strong>Detail Orientation</strong>:</div>
                  <div className="ml-4 mt-1">Excellence begins with details. At Servify, we follow a 'first time right' approach, digging deep to find root causes and spotting patterns that others may miss. This focus on detail builds trust and helps us deliver quality, always.</div>
                </li>
                <li>
                  <div>• <strong>Bias for Action</strong>:</div>
                  <div className="ml-4 mt-1">At Servify, we believe in moving with speed and purpose. A bias for action means stepping up, taking initiative, and making smart decisions quickly. We don't wait for perfect answers — we act, adapt, and keep building momentum together.</div>
                </li>
                <li>
                  <div>• <strong>Learn, Unlearn, Relearn</strong>:</div>
                  <div className="ml-4 mt-1">The world is always changing, and so are we. At Servify, we embrace learning every day, unlearning what no longer works, and relearning new ways to grow. This mindset keeps us relevant, adaptable, and future-ready.</div>
                </li>
                <li>
                  <div>• <strong>Focus, Specialize & Grow</strong>:</div>
                  <div className="ml-4 mt-1">At Servify, expertise drives growth. By focusing deeply on your domain and staying curious about new possibilities, you build authority and unlock innovation. When each of us grows in our craft, the whole team rises together.</div>
                </li>
              </ul>
            </div>

            <div className="p-4 bg-servify-purple/5 rounded-lg">
                              <h3 className="font-semibold text-servify-purple mb-2" data-testid="text-company-global">Global Presence</h3>
                <p className="text-servify-dark-grey text-sm">
                  Operating across India, the Middle East, Europe and North America with offices in Mumbai, Rotterdam, Seattle, Dallas, Istanbul and Dubai.
                </p>
            </div>
          </div>
          
          <Button 
            className="w-full mt-6 bg-servify-purple hover:bg-servify-purple-dark text-white"
            data-testid="button-learn-more"
            onClick={() => window.open('https://servify.com/in/', '_blank')}
          >
            Learn More About Servify
          </Button>
        </CardContent>
      </Card>

      {/* What to Expect on Your First Day at Servify */}
      <Card className="border-servify-light-grey" data-testid="welcome-team">
        <CardHeader className="border-b border-servify-light-grey">
          <CardTitle className="text-servify-purple">What to Expect on Your First Day at Servify</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="p-4 bg-servify-purple/5 rounded-lg">
              <p className="text-servify-dark-grey text-sm leading-relaxed">
                Your first day is all about <span className="text-servify-purple font-medium">getting comfortable, meeting your new colleagues, and understanding our mission</span> to revolutionize the device ownership experience. Here's a little of how you can prepare and what you can expect as you begin your journey with us.
              </p>
            </div>

            {/* Morning Section */}
            <div className="bg-white p-6 rounded-lg border border-servify-light-grey">
              <h3 className="font-semibold text-servify-purple mb-4 text-lg">Morning: Welcome and Setup</h3>
              <p className="text-servify-dark-grey text-sm leading-relaxed">
                Your first day will begin with a warm welcome from the HR team, who will assist you in setting up your equipment and access, including your laptop, email, and internal communication tools. You'll also receive a Welcome Kit filled with Servify goodies and an overview of our business and your role within it.
              </p>
            </div>

            {/* Mid-day Section */}
            <div className="bg-white p-6 rounded-lg border border-servify-light-grey">
              <h3 className="font-semibold text-servify-purple mb-4 text-lg">Mid-day: Meet the Team and Dive into the Servify World!</h3>
              <ul className="text-servify-dark-grey text-sm space-y-2">
                <li>• Connect with your new team members!</li>
                <li>• Look forward to a team lunch, held virtually or in-person, to get to know everyone in a relaxed atmosphere.</li>
                <li>• Meet your designated buddy, your primary contact for any questions.</li>
                <li>• Enquire about the exciting projects your team is working on.</li>
                <li>• Engage in a one-on-one meeting with your manager to clarify your role and initial responsibilities.</li>
                <li>• This meeting is crucial for understanding expectations and how to make a meaningful impact.</li>
                <li>• Focus on familiarizing yourself with the tools and processes at Servify with an overview of our key platforms.</li>
                <li>• Start exploring internal resources to get going.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paperwork Checklist */}
      <Card className="border-servify-light-grey" data-testid="paperwork-checklist">
        <CardHeader className="border-b border-servify-light-grey">
          <CardTitle className="text-servify-purple">Paperwork Checklist</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="p-4 bg-servify-purple/5 rounded-lg">
              <p className="text-servify-dark-grey text-sm leading-relaxed">
                Please ensure that you have the following documentation ready to submit on your first day at Servify.
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-servify-purple text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">1</span>
                <div>
                  <h4 className="font-semibold text-servify-dark-grey mb-1">Identity Documents</h4>
                  <p className="text-servify-dark-grey text-sm">Valid government-issued photo ID (Passport, Driver's License, or Aadhaar Card)</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-servify-purple text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">2</span>
                <div>
                  <h4 className="font-semibold text-servify-dark-grey mb-1">Educational Certificates</h4>
                  <p className="text-servify-dark-grey text-sm">Original or attested copies of your highest educational qualification</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-servify-purple text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">3</span>
                <div>
                  <h4 className="font-semibold text-servify-dark-grey mb-1">Employment Documents</h4>
                  <p className="text-servify-dark-grey text-sm">Previous employment certificates and relieving letters (if applicable)</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-servify-purple text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">4</span>
                <div>
                  <h4 className="font-semibold text-servify-dark-grey mb-1">Bank Account Details</h4>
                  <p className="text-servify-dark-grey text-sm">Cancelled cheque or bank passbook for salary processing</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-servify-purple text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">5</span>
                <div>
                  <h4 className="font-semibold text-servify-dark-grey mb-1">PAN Card</h4>
                  <p className="text-servify-dark-grey text-sm">Permanent Account Number card for tax purposes</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-servify-purple text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">6</span>
                <div>
                  <h4 className="font-semibold text-servify-dark-grey mb-1">Passport Size Photographs</h4>
                  <p className="text-servify-dark-grey text-sm">Recent passport-size photographs (2-3 copies)</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Your Cheatsheet */}
      <Card className="border-servify-light-grey" data-testid="cheatsheet">
        <CardHeader className="border-b border-servify-light-grey">
          <CardTitle className="text-servify-purple">Your Cheatsheet to the Servify Way!</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="p-4 bg-servify-purple/5 rounded-lg">
              <p className="text-servify-dark-grey text-sm leading-relaxed font-semibold">
                Begin with the right attitude: stay curious, enthusiastic, and open to learning. A positive mindset drives growth. Flip the cards to equip yourself for your first day.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Card 1: Come prepared to learn */}
              <div className="flip-card group cursor-pointer">
                <div className="flip-card-inner">
                  <div className="flip-card-front p-4 bg-white border border-servify-light-grey rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    {/* Flip Icon */}
                    <div className="absolute top-2 right-2 w-6 h-6 bg-servify-purple/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-servify-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    
                    {/* Main Icon */}
                    <div className="w-16 h-16 flex items-center justify-center">
                      <img 
                        src="/learning (2).png" 
                        alt="Learning" 
                        className="w-16 h-16 object-contain"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'block';
                          }
                        }}
                      />
                      <div className="w-16 h-16 bg-servify-purple/20 rounded-full flex items-center justify-center text-servify-purple text-2xl" style={{display: 'none'}}>
                        📚
                      </div>
                    </div>
                    

                  </div>
                  
                  <div className="flip-card-back p-4 bg-servify-purple text-white rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    <h4 className="font-semibold text-white mb-2">Come prepared to learn.</h4>
                    <p className="text-servify-white-grey text-sm leading-relaxed">Be ready to take in a lot of new information.</p>
                  </div>
                </div>
              </div>

              {/* Card 2: Ask questions */}
              <div className="flip-card group cursor-pointer">
                <div className="flip-card-inner">
                  <div className="flip-card-front p-4 bg-white border border-servify-light-grey rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    {/* Flip Icon */}
                    <div className="absolute top-2 right-2 w-6 h-6 bg-servify-purple/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-servify-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    
                    {/* Main Icon */}
                    <div className="w-16 h-16 flex items-center justify-center">
                      <img 
                        src="/conversation (2).png" 
                        alt="Conversation" 
                        className="w-16 h-16 object-contain"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'block';
                          }
                        }}
                      />
                      <div className="w-16 h-16 bg-servify-purple/20 rounded-full flex items-center justify-center text-servify-purple text-2xl" style={{display: 'none'}}>
                        ❓
                      </div>
                    </div>
                    

                  </div>
                  
                  <div className="flip-card-back p-4 bg-servify-purple text-white rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    <h4 className="font-semibold text-white mb-2">Ask questions.</h4>
                    <p className="text-servify-white-grey text-sm leading-relaxed">No question is too small. We're here to help you succeed.</p>
                  </div>
                </div>
              </div>

              {/* Card 3: Connect with your team */}
              <div className="flip-card group cursor-pointer">
                <div className="flip-card-inner">
                  <div className="flip-card-front p-4 bg-white border border-servify-light-grey rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    {/* Flip Icon */}
                    <div className="absolute top-2 right-2 w-6 h-6 bg-servify-purple/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-servify-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    
                    {/* Main Icon */}
                    <div className="w-16 h-16 flex items-center justify-center">
                      <img 
                        src="/connection (2).png" 
                        alt="Connection" 
                        className="w-16 h-16 object-contain"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'block';
                          }
                        }}
                      />
                      <div className="w-16 h-16 bg-servify-purple/20 rounded-full flex items-center justify-center text-servify-purple text-2xl" style={{display: 'none'}}>
                        🤝
                      </div>
                    </div>
                    

                  </div>
                  
                  <div className="flip-card-back p-4 bg-servify-purple text-white rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    <h4 className="font-semibold text-white mb-2">Connect with your team.</h4>
                    <p className="text-servify-white-grey text-sm leading-relaxed">Building relationships is a key part of our culture.</p>
                  </div>
                </div>
              </div>

              {/* Card 4: Be yourself */}
              <div className="flip-card group cursor-pointer">
                <div className="flip-card-inner">
                  <div className="flip-card-front p-4 bg-white border border-servify-light-grey rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    {/* Flip Icon */}
                    <div className="absolute top-2 right-2 w-6 h-6 bg-servify-purple/20 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-servify-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    
                    {/* Main Icon */}
                    <div className="w-16 h-16 flex items-center justify-center">
                      <img 
                        src="/user (2).png" 
                        alt="User" 
                        className="w-16 h-16 object-contain"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'block';
                          }
                        }}
                      />
                      <div className="w-16 h-16 bg-servify-purple/20 rounded-full flex items-center justify-center text-servify-purple text-2xl" style={{display: 'none'}}>
                        💡
                      </div>
                    </div>
                    

                  </div>
                  
                  <div className="flip-card-back p-4 bg-servify-purple text-white rounded-lg text-center h-48 flex flex-col items-center justify-center gap-4">
                    <p className="text-servify-white-grey text-sm leading-relaxed">Your unique background and perspective make our team stronger. Be authentic and share your ideas.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>




    </div>
  );
}
